import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';
import { TENANT_NEW_TERM_SCHOOL_CLASS_SETTING } from 'constants/GlobalConfig';

type TenantNewTermSchoolClassState = {
    isLoading: boolean;
    isSuccessful: boolean;
    tenantNewTermSchoolClass: TenantNewTermSchoolClass | undefined;
    newTermSchoolClass: NewTermSchoolClass | undefined;
};

export const useSaveTenantNewTermSchoolClass = () => {
    const [state, setState] = useState<TenantNewTermSchoolClassState>({
        isLoading: false,
        isSuccessful: false,
        tenantNewTermSchoolClass: undefined,
        newTermSchoolClass: undefined,
    });

    const tenantNewTermSchoolClassSettingControlSaveConnection = useAppSelector(
        (state) => state.tenantNewTermSchoolClassSettingControlSaveConnection
    );

    const newTermSchoolClassSettingSaveConnection = useAppSelector(
        (state) => state.newTermSchoolClassSettingSaveConnection
    );

    const dispatch = useDispatch();

    useEffect(() => {
        const { meta, payload } = tenantNewTermSchoolClassSettingControlSaveConnection;

        // 通信中でない === 画面遷移など意図しないタイミング
        if (!state.isLoading) return;

        switch (meta.status) {
            case Actions.statusEnum.SUCCESS: {
                setState((state) => ({
                    ...state,
                    isLoading: false,
                    isSuccessful: payload.is_successful,
                    tenantNewTermSchoolClass: convertResponseTenantNewTermSchoolClassSetting(payload.result),
                }));
                return;
            }
            case Actions.statusEnum.FAILURE:
            case Actions.statusEnum.ERROR: {
                setState((state) => ({
                    ...state,
                    isLoading: false,
                }));
                return;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantNewTermSchoolClassSettingControlSaveConnection]);

    useEffect(() => {
        const { meta, payload } = newTermSchoolClassSettingSaveConnection;

        // 通信中でない === 画面遷移など意図しないタイミング
        if (!state.isLoading) return;

        switch (meta.status) {
            case Actions.statusEnum.SUCCESS: {
                setState((state) => ({
                    ...state,
                    isLoading: false,
                    isSuccessful: payload.is_successful,
                    newTermSchoolClass: convertResponseNewTermSchoolClassSetting(payload.result),
                }));
                return;
            }
            case Actions.statusEnum.FAILURE:
            case Actions.statusEnum.ERROR: {
                setState((state) => ({
                    ...state,
                    isLoading: false,
                }));
                return;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newTermSchoolClassSettingSaveConnection]);

    const request = useCallback(
        (data: any, isControllableUser = false) => {
            isControllableUser
                ? dispatch(
                      Actions.http.connection.tenantNewTermSchoolClassSetting.controlSave(
                          dataConvert(data, isControllableUser)
                      )
                  )
                : dispatch(Actions.http.connection.newTermSchoolClassSetting.save(dataConvert(data)));

            setState((state) => ({
                ...state,
                isLoading: true,
            }));
        },
        [dispatch]
    );

    const reset = useCallback(() => {
        setState((state) => ({
            ...state,
            isLoading: false,
        }));
    }, []);

    return [state, request, reset] as const;
};

const dataConvert = (data: any, isControllableUser = false) => {
    const base = {
        uuid: data.uuid,
        visibility_timing: data.visibilityTiming,
        visibility_scheduled_month:
            data.visibilityTiming === TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_IMMEDIATE
                ? null
                : data.visibilityScheduledMonth,
        visibility_scheduled_day:
            data.visibilityTiming === TENANT_NEW_TERM_SCHOOL_CLASS_SETTING.VISIBILITY_TIMING_IMMEDIATE
                ? null
                : data.visibilityScheduledDay,
    };

    if (isControllableUser) {
        return {
            ...base,
            can_admin_update_visibility: data.canAdminUpdateVisibility,
            overwrite_admin_visibility_schedules: data.overwriteAdminVisibilitySchedules ?? false,
        };
    }
    return base;
};


type ResponseResultTenantNewTermSchoolClassSetting = Http.Connection.Response.TenantNewTermSchoolClassSetting.Control.Save['result'];
type ResponseResultNewTermSchoolClassSetting = Http.Connection.Response.NewTermSchoolClassSetting.Save['result']


const convertResponseTenantNewTermSchoolClassSetting = (tenantNewTermSchoolClassSetting: ResponseResultTenantNewTermSchoolClassSetting): TenantNewTermSchoolClass => {
    return {
        uuid: tenantNewTermSchoolClassSetting.uuid,
        visibilityTiming: tenantNewTermSchoolClassSetting.visibility_timing,
        visibilityScheduledMonth: tenantNewTermSchoolClassSetting.visibility_scheduled_month,
        visibilityScheduledDay: tenantNewTermSchoolClassSetting.visibility_scheduled_day,
        createdAt: tenantNewTermSchoolClassSetting.created_at,
        updatedAt: tenantNewTermSchoolClassSetting.updated_at,
        canAdminUpdateVisibility: tenantNewTermSchoolClassSetting.can_admin_update_visibility,
        overwriteAdminVisibilitySchedules: tenantNewTermSchoolClassSetting.overwrite_admin_visibility_schedules,
    };
};

const convertResponseNewTermSchoolClassSetting = (tenantNewTermSchoolClassSetting: ResponseResultNewTermSchoolClassSetting): NewTermSchoolClass => {
    return {
        uuid: tenantNewTermSchoolClassSetting.uuid,
        visibilityTiming: tenantNewTermSchoolClassSetting.visibility_timing,
        visibilityScheduledMonth: tenantNewTermSchoolClassSetting.visibility_scheduled_month,
        visibilityScheduledDay: tenantNewTermSchoolClassSetting.visibility_scheduled_day,
        createdAt: tenantNewTermSchoolClassSetting.created_at,
        updatedAt: tenantNewTermSchoolClassSetting.updated_at,
    };
};
